import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { appCrashLogger, appLogger, setRecentAction } from './app-logger.slice';
import { LogLevel } from 'services/logger.service';
import { serializeError } from 'serialize-error';
import { EnumDragger } from './app.slice';

export const useAppLogger = () => {
  const dragger = useAppSelector((state) => state.app.dragger);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setRecentAction({ key: 'EVENT', desc: `appInit` }));
    const cb = () => {
      dispatch(setRecentAction({ key: 'EVENT', desc: `beforeunload` }));
    };
    window.addEventListener('beforeunload', cb);
    return () => window.removeEventListener('beforeunload', cb);
  }, []);

  useEffect(() => {
    const rejectCb = (event: PromiseRejectionEvent) => {
      const reason = event.reason ?? 'Unknown rejection';
      const error = serializeError(reason);
      console.error('Unhandled promise rejection:', reason);
      dispatch(
        appLogger({
          level: LogLevel.Fatal,
          error: {
            name: 'Unhandled promise rejection: ' + error.name,
            message: error.message ?? '',
            stack: JSON.stringify(error),
          },
        })
      );
      event.preventDefault();
    };
    const errorCb = (event: ErrorEvent | Event) => {
      if (!(event instanceof ErrorEvent)) {
        // Resource load errors
        const target = (event as Event).target as HTMLElement & { src?: string; href?: string };
        const src = target?.src || target?.href || '(unknown URL)';
        console.error('Resource failed to load:', src, target);
        appLogger({
          level: LogLevel.Error,
          error: {
            name: 'ResourceLoadError',
            message: `Failed to load resource: ${src}`,
            stack: undefined,
          },
        });
        return;
      }

      const reason = event.error ?? event.message ?? 'Unknown error';
      const error = serializeError(reason);
      console.error('Unhandled error:', reason);
      dispatch(
        appLogger({
          level: LogLevel.Fatal,
          error: {
            name: 'Unhandled error: ' + error.name,
            message: error.message ?? '',
            stack: JSON.stringify(error),
          },
        })
      );

      event.preventDefault();
    };
    window.addEventListener('error', errorCb);
    window.addEventListener('unhandledrejection', rejectCb);
    return () => {
      window.removeEventListener('error', errorCb);
      window.removeEventListener('unhandledrejection', rejectCb);
    };
  }, []);

  useEffect(() => {
    if (dragger === null) return;
    dispatch(setRecentAction({ key: 'EVENT', desc: `draggerSTART: ${EnumDragger[dragger]}` }));
    return () => {
      if (dragger === null) return;
      dispatch(setRecentAction({ key: 'EVENT', desc: `draggerEND: ${EnumDragger[dragger]}` }));
    };
  }, [dragger]);

  return useCallback((error: Error) => {
    console.log('ErrorBoundary', error);
    dispatch(appCrashLogger({ error }));
  }, []);
};
